import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";

export default function PrintLabelsDialog({ open, onClose, listener, rows = [] }) {
  const [template, setTemplate] = useState("5160");
  const [startPosition, setStartPosition] = useState(1);
  const [includePrice, setIncludePrice] = useState(true);
  const [includeBarcode, setIncludeBarcode] = useState(true);
  const [quantityPerItem, setQuantityPerItem] = useState(1);

  const templateConfig = useMemo(() => (
    template === "5167" ? { columns: 4, perSheet: 80 } : { columns: 3, perSheet: 30 }
  ), [template]);

  const maxPosition = templateConfig.perSheet;

  const handleConfirm = () => {
    listener && listener({
      template,
      startPosition: Math.max(1, Math.min(maxPosition, parseInt(startPosition || 1, 10))),
      includePrice,
      includeBarcode,
      quantityPerItem: Math.max(1, parseInt(quantityPerItem || 1, 10)),
    });
    onClose && onClose();
  };

  const previewItems = useMemo(() => {
    const q = Math.max(1, parseInt(quantityPerItem || 1, 10));
    const expanded = rows.flatMap(r => Array.from({ length: q }, () => r));
    return expanded.slice(0, 6);
  }, [rows, quantityPerItem]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Print Labels</DialogTitle>
      <DialogContent>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="template-select-label">Template</InputLabel>
            <Select labelId="template-select-label" value={template} label="Template" onChange={(e) => setTemplate(e.target.value)}>
              <MenuItem value="5160">Avery 5160 (30 labels)</MenuItem>
              <MenuItem value="5167">Avery 5167 (80 labels)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            type="number"
            label="Start position"
            inputProps={{ min: 1, max: maxPosition }}
            value={startPosition}
            onChange={(e) => setStartPosition(e.target.value)}
            fullWidth
          />
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 2 }}>
          <FormControlLabel control={<Checkbox checked={includePrice} onChange={(e) => setIncludePrice(e.target.checked)} />} label="Include price" />
          <FormControlLabel control={<Checkbox checked={includeBarcode} onChange={(e) => setIncludeBarcode(e.target.checked)} />} label="Include barcode (SKU)" />
          <TextField
            type="number"
            label="Quantity per item"
            inputProps={{ min: 1 }}
            value={quantityPerItem}
            onChange={(e) => setQuantityPerItem(e.target.value)}
            sx={{ minWidth: 160 }}
          />
        </Stack>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Preview</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${templateConfig.columns}, 1fr)`, gap: 1, border: "1px solid #eee", p: 1, borderRadius: 1 }}>
            {Array.from({ length: Math.min(Math.max(startPosition - 1, 0), templateConfig.columns) }).map((_, idx) => (
              <Box key={`pad-${idx}`} sx={{ border: "1px dashed #ddd", p: 1, borderRadius: 1, bgcolor: "#fafafa" }} />
            ))}
            {previewItems.map((r, idx) => {
              const model = r.modelNumber || r.model || "";
              const sku = r.sku || r.internalUpc || "";
              const price = r.listPrice || r.price || "";
              return (
                <Box key={`prev-${idx}`} sx={{ border: "1px solid #ddd", p: 1, borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{model}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>SKU: {sku}</Typography>
                  {includePrice && <Typography variant="body2">{price ? `$${price}` : ``}</Typography>}
                  {includeBarcode && <Box sx={{ mt: 0.5, height: 20, bgcolor: "#e0e0e0", borderRadius: 0.5 }} />}
                </Box>
              );
            })}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            Note: Preview is approximate; printed output aligns to template.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm}>Print</Button>
      </DialogActions>
    </Dialog>
  );
}
